import { useState, useEffect, useCallback, forwardRef } from 'react';
import axios from 'axios';
import {
  Box, Stack, Typography, Button, IconButton, Chip,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel,
  Divider, Tooltip, CircularProgress, Paper, Collapse, Tab, Tabs,
} from '@mui/material';
import { PlusIcon, PencilIcon, TrashIcon, ChevronDownIcon, ChevronRightIcon, SquaresPlusIcon, Bars3Icon, Cog6ToothIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const TYPE_LABELS = { unica: 'Única', multiple: 'Múltiple', abierta: 'Abierta', fotos: 'Fotos' };
const TYPE_COLORS = { unica: 'primary', multiple: 'secondary', abierta: 'success', fotos: 'warning' };
const company = () => sessionStorage.getItem('company');

// ─── LoadingButton ────────────────────────────────────────────────────────────
function LoadingButton({ loading, children, startIcon, ...props }) {
  return (
    <Button
      {...props}
      disabled={props.disabled || loading}
      startIcon={loading ? <CircularProgress size={16} color="inherit" /> : startIcon}
    >
      {children}
    </Button>
  );
}

// ─── LoadingIconButton ────────────────────────────────────────────────────────
const LoadingIconButton = forwardRef(function LoadingIconButton({ loading, children, ...props }, ref) {
  return (
    <IconButton ref={ref} {...props} disabled={props.disabled || loading}>
      {loading ? <CircularProgress size={16} /> : children}
    </IconButton>
  );
});

// ─── Evaluation Colors & Types ────────────────────────────────────────────────
const EVAL_COLORS = {
  buena: '#10b981',
  regular: '#f59e0b',
  mala: '#ef4444',
  na: '#64748b'
};

const isStandardEvalText = (text) => {
  const norm = (text || '').trim().toLowerCase();
  return (
    norm === 'bueno' || norm === 'buena' || norm === 'buenos' || norm === 'buenas' ||
    norm === 'regular' || norm === 'regulares' ||
    norm === 'malo' || norm === 'mala' || norm === 'malos' || norm === 'malas'
  );
};

const getAutoEvalType = (text) => {
  const norm = (text || '').trim().toLowerCase();
  if (norm === 'bueno' || norm === 'buena' || norm === 'buenos' || norm === 'buenas') return 'buena';
  if (norm === 'regular' || norm === 'regulares') return 'regular';
  if (norm === 'malo' || norm === 'mala' || norm === 'malos' || norm === 'malas') return 'mala';
  return null;
};

// ─── OptionRow ────────────────────────────────────────────────────────────────
function OptionRow({ option, onChange, onRemove }) {
  const isCheckedPhoto = option.requires_photo === true || option.requires_photo === 1;
  const isCheckedJustify = option.requires_justification === true || option.requires_justification === 1;

  const isStandard = isStandardEvalText(option.text);
  const autoType = getAutoEvalType(option.text);
  const evalType = autoType || option.evaluation_type || 'buena';
  const color = option.color || EVAL_COLORS[evalType] || '#10b981';

  const handleTextChange = (newText) => {
    const detectedType = getAutoEvalType(newText);
    const newEval = detectedType || option.evaluation_type || 'buena';
    onChange({
      ...option,
      text: newText,
      evaluation_type: newEval,
      color: detectedType ? EVAL_COLORS[detectedType] : (option.color || EVAL_COLORS[newEval]),
    });
  };

  const handleEvalChange = (newType) => {
    onChange({
      ...option,
      evaluation_type: newType,
      color: EVAL_COLORS[newType] || '#10b981',
    });
  };

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ borderBottom: '1px solid #f1f5f9', pb: 1, '&:last-child': { borderBottom: 'none', pb: 0 } }}>
      <TextField 
        size="small" 
        placeholder="Texto de la opción (ej. Bueno, Conforme, Malo)" 
        value={option.text}
        onChange={(e) => handleTextChange(e.target.value)} 
        sx={{ flex: isStandard ? 2.5 : 1.5 }} 
      />
      {!isStandard && (
        <>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel sx={{ fontSize: '0.75rem' }}>Evaluación</InputLabel>
            <Select
              label="Evaluación"
              value={evalType}
              onChange={(e) => handleEvalChange(e.target.value)}
              sx={{ fontSize: '0.75rem', height: 38 }}
            >
              <MenuItem value="buena" sx={{ fontSize: '0.75rem', color: '#10b981', fontWeight: 700 }}>🟢 Buena</MenuItem>
              <MenuItem value="regular" sx={{ fontSize: '0.75rem', color: '#f59e0b', fontWeight: 700 }}>🟡 Regular</MenuItem>
              <MenuItem value="mala" sx={{ fontSize: '0.75rem', color: '#ef4444', fontWeight: 700 }}>🔴 Mala</MenuItem>
              <MenuItem value="na" sx={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 700 }}>⚪ Neutro / NA</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Color identificador en el reporte">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <input
                type="color"
                value={color}
                onChange={(e) => onChange({ ...option, color: e.target.value })}
                style={{
                  width: 34,
                  height: 34,
                  padding: 0,
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  cursor: 'pointer',
                  backgroundColor: color,
                }}
              />
            </Box>
          </Tooltip>
        </>
      )}
      <Stack spacing={0.2} sx={{ minWidth: 140 }}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={isCheckedPhoto}
              onChange={(e) => onChange({ ...option, requires_photo: e.target.checked })}
              sx={{ p: 0.2 }}
            />
          }
          label="Requiere foto"
          sx={{ mx: 0, '& .MuiFormControlLabel-label': { fontSize: '0.72rem' } }}
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={isCheckedJustify}
              onChange={(e) => onChange({ ...option, requires_justification: e.target.checked })}
              sx={{ p: 0.2 }}
            />
          }
          label="Requiere justificación"
          sx={{ mx: 0, '& .MuiFormControlLabel-label': { fontSize: '0.72rem' } }}
        />
      </Stack>
      <IconButton size="small" color="error" onClick={onRemove}><TrashIcon className="h-4 w-4" /></IconButton>
    </Stack>
  );
}

// ─── QuestionDialog ───────────────────────────────────────────────────────────
function QuestionDialog({ open, onClose, onSave, initial, optionTemplates, availableQuestions }) {
  const [form, setForm] = useState({ text: '', type: 'unica', min_photos: null, max_photos: null, options: [] });
  const [saving, setSaving] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [copyPickerOpen, setCopyPickerOpen] = useState(false);
  const [copySearch, setCopySearch] = useState('');
  const [copyType, setCopyType] = useState('all');
  const [copyGroup, setCopyGroup] = useState('all');

  const copyTypeOptions = [...new Map((availableQuestions || []).map(q => [q._typeKey, q._typeName])).entries()];
  const copyGroupOptions = [...new Map((availableQuestions || [])
    .filter(q => copyType === 'all' || q._typeKey === copyType)
    .map(q => [q._groupId, q._groupName])).entries()];
  const filteredCopyQuestions = (availableQuestions || []).filter(q => {
    if (copyType !== 'all' && q._typeKey !== copyType) return false;
    if (copyGroup !== 'all' && q._groupId !== copyGroup) return false;
    const s = copySearch.trim().toLowerCase();
    return !s || (q.text || '').toLowerCase().includes(s);
  });

  useEffect(() => {
    if (open) {
      const sortById = (opts) => (opts || []).slice().sort((a, b) => (a.id || 0) - (b.id || 0));
      setForm(initial
        ? { 
            ...initial, 
            min_photos: initial.type === 'fotos' ? (initial.min_photos || 1) : null,
            max_photos: initial.type === 'fotos' ? (initial.max_photos || 5) : null,
            options: sortById(initial.options) 
          }
        : { text: '', type: 'unica', min_photos: null, max_photos: null, options: [] });
      setSaving(false);
      setCopyPickerOpen(false);
    }
  }, [open, initial]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const addOption = () => set('options', [...form.options, { text: '', requires_photo: false, requires_justification: false, evaluation_type: 'buena', color: '#10b981' }]);
  const updateOption = (i, val) => set('options', form.options.map((o, idx) => idx === i ? val : o));
  const removeOption = (i) => set('options', form.options.filter((_, idx) => idx !== i));
  const loadTemplate = (tpl) => { 
    set('options', tpl.options.map(o => ({ 
      text: o.text, 
      requires_photo: !!o.requires_photo, 
      requires_justification: !!o.requires_justification,
      evaluation_type: o.evaluation_type || 'buena',
      color: o.color || EVAL_COLORS[o.evaluation_type] || '#10b981',
    }))); 
    setTplOpen(false); 
  };

  const handleCopyFromQuestion = (q) => {
    setForm({
      text: q.text,
      type: q.type,
      min_photos: q.type === 'fotos' ? (q.min_photos || 1) : null,
      max_photos: q.type === 'fotos' ? (q.max_photos || 5) : null,
      options: (q.options || []).map(o => ({
        text: o.text,
        requires_photo: o.requires_photo === 1 || o.requires_photo === true,
        requires_justification: o.requires_justification === 1 || o.requires_justification === true,
        evaluation_type: o.evaluation_type || 'buena',
        color: o.color || EVAL_COLORS[o.evaluation_type] || '#10b981',
      }))
    });
    setCopyPickerOpen(false);
  };

  const isChoice = form.type === 'unica' || form.type === 'multiple';
  const valid = form.text.trim() && (
    (isChoice && form.options.length > 0 && form.options.every(o => o.text.trim())) ||
    form.type === 'abierta' ||
    (form.type === 'fotos' && (form.min_photos || 1) >= 1 && (form.max_photos || 5) >= (form.min_photos || 1))
  );

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(form); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{initial ? 'Editar pregunta' : 'Nueva pregunta'}</span>
        {availableQuestions?.length > 0 && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<DocumentDuplicateIcon className="h-4 w-4" />}
            onClick={() => setCopyPickerOpen(true)}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Copiar de pregunta existente
          </Button>
        )}
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField label="Texto de la pregunta" multiline rows={2} fullWidth value={form.text}
            onChange={(e) => set('text', e.target.value)} />
          <FormControl fullWidth size="small">
            <InputLabel>Tipo de respuesta</InputLabel>
            <Select 
              value={form.type} 
              label="Tipo de respuesta" 
              onChange={(e) => {
                const newType = e.target.value;
                setForm(f => ({
                  ...f,
                  type: newType,
                  min_photos: newType === 'fotos' ? (f.min_photos || 1) : null,
                  max_photos: newType === 'fotos' ? (f.max_photos || 5) : null,
                }));
              }}
            >
              <MenuItem value="unica">Única</MenuItem>
              <MenuItem value="multiple">Múltiple</MenuItem>
              <MenuItem value="abierta">Abierta</MenuItem>
              <MenuItem value="fotos">Fotos</MenuItem>
            </Select>
          </FormControl>

          {form.type === 'fotos' && (
            <Stack direction="row" spacing={2}>
              <TextField label="Mínimo de fotos" type="number" size="small" value={form.min_photos || 1}
                onChange={(e) => set('min_photos', Number(e.target.value))} inputProps={{ min: 1 }} sx={{ flex: 1 }} />
              <TextField label="Máximo de fotos" type="number" size="small" value={form.max_photos || 5}
                onChange={(e) => set('max_photos', Number(e.target.value))} inputProps={{ min: 1 }} sx={{ flex: 1 }} />
            </Stack>
          )}

          {isChoice && (
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={700}>Opciones de respuesta</Typography>
                <Stack direction="row" spacing={1}>
                  {optionTemplates?.length > 0 && (
                    <Button size="small" variant="outlined" startIcon={<SquaresPlusIcon className="h-4 w-4" />}
                      onClick={() => setTplOpen(true)} sx={{ borderRadius: 2 }}>
                      Cargar plantilla
                    </Button>
                  )}
                  <Button size="small" startIcon={<PlusIcon className="h-4 w-4" />} onClick={addOption}>
                    Agregar opción
                  </Button>
                </Stack>
              </Stack>
              <Stack spacing={1}>
                {form.options.length === 0 && (
                  <Typography variant="caption" color="text.secondary">Agrega al menos una opción.</Typography>
                )}
                {form.options.map((opt, i) => (
                  <OptionRow key={i} option={opt} onChange={(val) => updateOption(i, val)} onRemove={() => removeOption(i)} />
                ))}
              </Stack>

              {/* Template picker */}
              <Dialog open={tplOpen} onClose={() => setTplOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 700 }}>Seleccionar plantilla</DialogTitle>
                <Divider />
                <DialogContent>
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {optionTemplates?.map((tpl) => (
                      <Paper key={tpl.id} variant="outlined" sx={{ p: 1.5, cursor: 'pointer', borderRadius: 2, '&:hover': { bgcolor: '#f0f7ff', borderColor: '#3b82f6' } }}
                        onClick={() => loadTemplate(tpl)}>
                        <Typography fontWeight={600} variant="body2">{tpl.name}</Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                          {tpl.options?.map((o, i) => (
                            <Chip key={i} label={`${o.text}${Number(o.requires_photo) === 1 ? ' 📷' : ''}${Number(o.requires_justification) === 1 ? ' ✍️' : ''}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                          ))}
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </DialogContent>
              </Dialog>
            </Box>
          )}

          {form.type === 'abierta' && (
            <TextField label="Campo de texto (vista previa)" multiline rows={2} fullWidth disabled
              placeholder="El usuario escribirá su respuesta aquí..." />
          )}
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <LoadingButton variant="contained" loading={saving} disabled={!valid} onClick={handleSave} sx={{ borderRadius: 2 }}>
          Guardar
        </LoadingButton>
      </DialogActions>
    </Dialog>

    {/* Copy existing question dialog */}
    <Dialog open={copyPickerOpen} onClose={() => setCopyPickerOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>Seleccionar pregunta a copiar</DialogTitle>
      <Divider />
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Selecciona cualquier pregunta (de cualquier grupo o tipo de sistema) para copiar su enunciado y sus opciones:
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
          <TextField size="small" placeholder="Buscar pregunta..." value={copySearch}
            onChange={e => setCopySearch(e.target.value)} sx={{ flex: 1 }} />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Tipo de sistema</InputLabel>
            <Select value={copyType} label="Tipo de sistema"
              onChange={e => { setCopyType(e.target.value); setCopyGroup('all'); }}>
              <MenuItem value="all">Todos</MenuItem>
              {copyTypeOptions.map(([k, name]) => <MenuItem key={k} value={k}>{name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Grupo</InputLabel>
            <Select value={copyGroup} label="Grupo" onChange={e => setCopyGroup(e.target.value)}>
              <MenuItem value="all">Todos</MenuItem>
              {copyGroupOptions.map(([id, name]) => <MenuItem key={id} value={id}>{name}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>
        <Stack spacing={1.5}>
          {filteredCopyQuestions.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No se encontraron preguntas.
            </Typography>
          )}
          {filteredCopyQuestions.map((q) => (
            <Paper
              key={q.id}
              variant="outlined"
              sx={{
                p: 1.5,
                cursor: 'pointer',
                borderRadius: 2,
                '&:hover': { bgcolor: '#f0f7ff', borderColor: '#3b82f6' }
              }}
              onClick={() => handleCopyFromQuestion(q)}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Chip label={TYPE_LABELS[q.type]} color={TYPE_COLORS[q.type]} size="small" sx={{ fontWeight: 700, fontSize: '0.65rem' }} />
                <Typography fontWeight={600} variant="body2">{q.text}</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {q._typeName} · {q._groupName}
              </Typography>
              {(q.type === 'unica' || q.type === 'multiple') && q.options?.length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                  {q.options.map((o, idx) => (
                    <Chip
                      key={idx}
                      label={`${o.text}${Number(o.requires_photo) === 1 ? ' 📷' : ''}${Number(o.requires_justification) === 1 ? ' ✍️' : ''}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  ))}
                </Stack>
              )}
            </Paper>
          ))}
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={() => setCopyPickerOpen(false)}>Cancelar</Button>
      </DialogActions>
    </Dialog>
  </>
  );
}

// ─── SortableQuestion ────────────────────────────────────────────────────────
function SortableQuestion({ q, index, onEdit, onCopy, copyingId, deletingId, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: q.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  return (
    <Paper ref={setNodeRef} style={style} elevation={0} sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2 }}>
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Box {...attributes} {...listeners} sx={{ cursor: 'grab', pt: 0.5, color: '#94a3b8', '&:active': { cursor: 'grabbing' } }}>
          <Bars3Icon style={{ width: 16, height: 16 }} />
        </Box>
        <Box sx={{ minWidth: 26, textAlign: 'center', fontWeight: 800, color: '#475569', fontSize: '0.85rem', pt: 0.3 }}>
          #{index !== undefined ? index + 1 : ''}
        </Box>
        <Chip label={TYPE_LABELS[q.type]} color={TYPE_COLORS[q.type]} size="small" sx={{ minWidth: 70, fontWeight: 700 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight={600}>{q.text}</Typography>
          {q.type === 'fotos' && (<Typography variant="caption" color="text.secondary">Fotos: mín {q.min_photos} – máx {q.max_photos}</Typography>)}
          {(q.type === 'unica' || q.type === 'multiple') && q.options?.length > 0 && (
            <Stack direction="row" spacing={0.6} flexWrap="wrap" sx={{ mt: 0.5 }}>
               {q.options.map(o => (
                 <Chip 
                   key={o.id || o.text} 
                   label={
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                       {o.color && (
                         <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: o.color }} />
                       )}
                       <span>{`${o.text}${Number(o.requires_photo) === 1 ? ' 📷' : ''}${Number(o.requires_justification) === 1 ? ' ✍️' : ''}`}</span>
                     </Box>
                   } 
                   size="small" 
                   variant="outlined" 
                   sx={{ 
                     fontSize: '0.7rem',
                     borderColor: o.color ? `${o.color}80` : '#cbd5e1',
                     bgcolor: o.color ? `${o.color}15` : 'transparent',
                     color: '#0f172a',
                     fontWeight: 600
                   }} 
                 />
               ))}
            </Stack>
          )}
        </Box>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Copiar pregunta">
            <LoadingIconButton size="small" color="primary" loading={copyingId === q.id} onClick={() => onCopy(q)}>
              <DocumentDuplicateIcon className="h-4 w-4" />
            </LoadingIconButton>
          </Tooltip>
          <Tooltip title="Editar pregunta">
            <IconButton size="small" onClick={() => onEdit(q)}><PencilIcon className="h-4 w-4" /></IconButton>
          </Tooltip>
          <Tooltip title="Eliminar pregunta">
            <LoadingIconButton size="small" color="error" loading={deletingId === q.id} onClick={() => onDelete(q.id)}>
              <TrashIcon className="h-4 w-4" />
            </LoadingIconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
}

// ─── GroupCard ────────────────────────────────────────────────────────────────
const sortQuestionsAscending = (list) => (list || []).slice().sort((a, b) => {
  const oA = a.order ?? 0;
  const oB = b.order ?? 0;
  if (oA !== oB) return oA - oB;
  return (a.id || 0) - (b.id || 0);
});

function GroupCard({ group, onGroupEdited, onGroupDeleted, optionTemplates, dragHandleProps, allQuestions, onQuestionsChange }) {
  const [expanded, setExpanded] = useState(false);
  const [qDialog, setQDialog] = useState({ open: false, editing: null });
  const [savingQ, setSavingQ] = useState(false);
  const [copyingId, setCopyingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deletingGroup, setDeletingGroup] = useState(false);
  const [questions, setQuestions] = useState(() => sortQuestionsAscending(group.questions));

  useEffect(() => {
    if (group.questions) {
      setQuestions(sortQuestionsAscending(group.questions));
    }
  }, [group.questions]);

  useEffect(() => {
    if (onQuestionsChange && questions !== group.questions) onQuestionsChange(group.id, questions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions]);

  const handleCopyQuestion = async (q) => {
    setCopyingId(q.id);
    try {
      const res = await axios.post(`/copyQuestion/${q.id}`);
      setQuestions(prev => sortQuestionsAscending([...prev, res.data.data]));
    } catch (e) {
      console.error(e);
    } finally {
      setCopyingId(null);
    }
  };

  const handleSaveQuestion = async (form) => {
    setSavingQ(true);
    try {
      const payload = { 
        ...form, 
        min_photos: form.type === 'fotos' ? (Number(form.min_photos) || 1) : null,
        max_photos: form.type === 'fotos' ? (Number(form.max_photos) || null) : null,
        group_id: group.id, 
        options: (form.options || []).map(o => {
          const auto = getAutoEvalType(o.text);
          const finalEval = auto || o.evaluation_type || 'buena';
          return { 
            text: o.text, 
            requires_photo: o.requires_photo ? 1 : 0, 
            requires_justification: o.requires_justification ? 1 : 0,
            evaluation_type: finalEval,
            color: o.color || EVAL_COLORS[finalEval] || '#10b981'
          };
        }) 
      };
      if (qDialog.editing) {
        const res = await axios.put(`/updateQuestion/${qDialog.editing.id}`, payload);
        setQuestions(prev => sortQuestionsAscending(prev.map(q => q.id === qDialog.editing.id ? res.data.data : q)));
      } else {
        const res = await axios.post('/saveQuestion', payload);
        setQuestions(prev => sortQuestionsAscending([...prev, res.data.data]));
      }
      setQDialog({ open: false, editing: null });
    } catch (e) { console.error(e); }
    finally { setSavingQ(false); }
  };

  const handleDeleteQuestion = async (id) => {
    if (!window.confirm('¿Eliminar esta pregunta?')) return;
    setDeletingId(id);
    try {
      await axios.delete(`/deleteQuestion/${id}`);
      setQuestions(prev => prev.filter(q => q.id !== id));
    } catch (e) { console.error(e); }
    finally { setDeletingId(null); }
  };

  const handleDeleteGroup = async () => {
    if (!window.confirm('¿Eliminar este grupo y todas sus preguntas?')) return;
    setDeletingGroup(true);
    try { await onGroupDeleted(group.id); }
    finally { setDeletingGroup(false); }
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleQDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = questions.findIndex(q => q.id === active.id);
    const newIdx = questions.findIndex(q => q.id === over.id);
    const reordered = arrayMove(questions, oldIdx, newIdx);
    setQuestions(reordered);
    await axios.post('/reorderQuestions', { order: reordered.map((q, i) => ({ id: q.id, order: i })) });
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
      <Stack direction="row" alignItems="center"
        sx={{ px: 2.5, py: 1.5, '&:hover': { bgcolor: '#f8fafc' } }}>
        <Box {...(dragHandleProps || {})} sx={{ cursor: 'grab', mr: 1, color: '#94a3b8', '&:active': { cursor: 'grabbing' } }}
          onClick={e => e.stopPropagation()}>
          <Bars3Icon style={{ width: 18, height: 18 }} />
        </Box>
        <Box sx={{ cursor: 'pointer', flex: 1, display: 'flex', alignItems: 'center' }} onClick={() => setExpanded(v => !v)}>
          <IconButton size="small" sx={{ mr: 1 }}>{expanded ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}</IconButton>
          <Typography fontWeight={700} sx={{ flex: 1 }}>{group.name}</Typography>
        </Box>
        <Chip label={`${questions.length} pregunta${questions.length !== 1 ? 's' : ''}`} size="small" sx={{ mr: 1 }} />
        <Tooltip title="Editar grupo">
          <IconButton size="small" onClick={e => { e.stopPropagation(); onGroupEdited(group); }}>
            <PencilIcon className="h-4 w-4" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Eliminar grupo">
          <LoadingIconButton size="small" color="error" loading={deletingGroup} onClick={e => { e.stopPropagation(); handleDeleteGroup(); }}>
            <TrashIcon className="h-4 w-4" />
          </LoadingIconButton>
        </Tooltip>
      </Stack>

      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ px: 3, py: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2" color="text.secondary">Preguntas del grupo (Orden Ascendente)</Typography>
            <LoadingButton size="small" variant="outlined" loading={savingQ}
              startIcon={<PlusIcon className="h-4 w-4" />}
              onClick={() => setQDialog({ open: true, editing: null })} sx={{ borderRadius: 2 }}>
              Agregar pregunta
            </LoadingButton>
          </Stack>

          {questions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              Este grupo no tiene preguntas aún.
            </Typography>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleQDragEnd}>
              <SortableContext items={questions.map(q => q.id)} strategy={verticalListSortingStrategy}>
                <Stack spacing={1.5}>
                  {questions.map((q, idx) => (
                    <SortableQuestion key={q.id} q={q} index={idx}
                      onEdit={q => setQDialog({ open: true, editing: q })}
                      onCopy={handleCopyQuestion}
                      copyingId={copyingId}
                      deletingId={deletingId}
                      onDelete={handleDeleteQuestion} />
                  ))}
                </Stack>
              </SortableContext>
            </DndContext>
          )}
        </Box>
      </Collapse>

      <QuestionDialog open={qDialog.open} onClose={() => setQDialog({ open: false, editing: null })}
        onSave={handleSaveQuestion} initial={qDialog.editing} optionTemplates={optionTemplates} availableQuestions={allQuestions || questions} />
    </Paper>
  );
}

// ─── OptionTemplateManager ────────────────────────────────────────────────────
function OptionTemplateManager({ templates, onTemplatesChange }) {
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [name, setName] = useState('');
  const [options, setOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const openNew = () => { setName(''); setOptions([]); setDialog({ open: true, editing: null }); };
  const openEdit = (tpl) => {
    setName(tpl.name);
    const sorted = (tpl.options || []).slice().sort((a, b) => (a.id || 0) - (b.id || 0));
    setOptions(sorted.map(o => ({ ...o })));
    setDialog({ open: true, editing: tpl });
  };
  const addOpt = () => setOptions(prev => [...prev, { text: '', requires_photo: false, requires_justification: false, evaluation_type: 'buena', color: '#10b981' }]);
  const updateOpt = (i, val) => setOptions(prev => prev.map((o, idx) => idx === i ? val : o));
  const removeOpt = (i) => setOptions(prev => prev.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { 
        name, 
        company: company(), 
        options: options.map(o => {
          const auto = getAutoEvalType(o.text);
          const finalEval = auto || o.evaluation_type || 'buena';
          return {
            text: o.text,
            requires_photo: o.requires_photo ? 1 : 0,
            requires_justification: o.requires_justification ? 1 : 0,
            evaluation_type: finalEval,
            color: o.color || EVAL_COLORS[finalEval] || '#10b981'
          };
        })
      };
      if (dialog.editing) {
        const res = await axios.put(`/updateOptionTemplateGroup/${dialog.editing.id}`, payload);
        onTemplatesChange(prev => prev.map(t => t.id === dialog.editing.id ? res.data.data : t));
      } else {
        const res = await axios.post('/saveOptionTemplateGroup', payload);
        onTemplatesChange(prev => [res.data.data, ...prev]);
      }
      setDialog({ open: false, editing: null });
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta plantilla?')) return;
    setDeletingId(id);
    try {
      await axios.delete(`/deleteOptionTemplateGroup/${id}`);
      onTemplatesChange(prev => prev.filter(t => t.id !== id));
    } catch (e) { console.error(e); }
    finally { setDeletingId(null); }
  };

  const valid = name.trim() && options.length > 0 && options.every(o => o.text.trim());

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Plantillas de opciones</Typography>
          <Typography variant="body2" color="text.secondary">
            Crea grupos reutilizables (ej. "Condición": Bueno / Regular / Malo).
          </Typography>
        </Box>
        <LoadingButton variant="contained" loading={false} startIcon={<PlusIcon className="h-5 w-5" />}
          onClick={openNew} sx={{ borderRadius: 2, px: 3 }}>
          Nueva plantilla
        </LoadingButton>
      </Stack>

      {templates.length === 0 ? (
        <Paper elevation={0} sx={{ p: 5, textAlign: 'center', border: '2px dashed #e2e8f0', borderRadius: 4 }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>Sin plantillas. Crea una para reutilizar en preguntas.</Typography>
          <Button variant="outlined" startIcon={<PlusIcon className="h-4 w-4" />} onClick={openNew}>Crear plantilla</Button>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {templates.map(tpl => (
            <Paper key={tpl.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack direction="row" alignItems="center">
                <Box sx={{ flex: 1 }}>
                  <Typography fontWeight={700} variant="body1">{tpl.name}</Typography>
                  <Stack direction="row" spacing={0.6} flexWrap="wrap" sx={{ mt: 0.5 }}>
                    {tpl.options?.map((o, i) => (
                      <Chip 
                        key={i} 
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {o.color && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: o.color }} />}
                            <span>{`${o.text}${o.requires_photo ? ' 📷' : ''}${o.requires_justification ? ' ✍️' : ''}`}</span>
                          </Box>
                        }
                        size="small" 
                        variant="outlined" 
                        sx={{ 
                          fontSize: '0.7rem',
                          borderColor: o.color ? `${o.color}80` : '#cbd5e1',
                          bgcolor: o.color ? `${o.color}15` : 'transparent',
                          fontWeight: 600
                        }} 
                      />
                    ))}
                  </Stack>
                </Box>
                <Stack direction="row" spacing={0.5}>
                  <IconButton size="small" onClick={() => openEdit(tpl)}><PencilIcon className="h-4 w-4" /></IconButton>
                  <LoadingIconButton size="small" color="error" loading={deletingId === tpl.id} onClick={() => handleDelete(tpl.id)}>
                    <TrashIcon className="h-4 w-4" />
                  </LoadingIconButton>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      <Dialog open={dialog.open} onClose={() => setDialog({ open: false, editing: null })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{dialog.editing ? 'Editar plantilla' : 'Nueva plantilla de opciones'}</DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Nombre de la plantilla (ej. Condición)" fullWidth value={name} onChange={e => setName(e.target.value)} />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2" fontWeight={700}>Opciones</Typography>
              <Button size="small" startIcon={<PlusIcon className="h-4 w-4" />} onClick={addOpt}>Agregar</Button>
            </Stack>
            {options.length === 0 && <Typography variant="caption" color="text.secondary">Agrega al menos una opción.</Typography>}
            {options.map((opt, i) => (
              <OptionRow key={i} option={opt} onChange={val => updateOpt(i, val)} onRemove={() => removeOpt(i)} />
            ))}
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialog({ open: false, editing: null })} disabled={saving}>Cancelar</Button>
          <LoadingButton variant="contained" loading={saving} disabled={!valid} onClick={handleSave} sx={{ borderRadius: 2 }}>
            Guardar
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── GroupsDndList ────────────────────────────────────────────────────────────
function SortableGroupCard({ group, onGroupEdited, onGroupDeleted, optionTemplates, allQuestions, onQuestionsChange }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: group.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style}>
      <GroupCard group={group} onGroupEdited={onGroupEdited} onGroupDeleted={onGroupDeleted}
        optionTemplates={optionTemplates} allQuestions={allQuestions} onQuestionsChange={onQuestionsChange}
        dragHandleProps={{ ...attributes, ...listeners }} />
    </div>
  );
}

function GroupsDndList({ groups, onReorder, onGroupEdited, onGroupDeleted, optionTemplates, allQuestions, onQuestionsChange }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = groups.findIndex(g => g.id === active.id);
    const newIdx = groups.findIndex(g => g.id === over.id);
    const reordered = arrayMove(groups, oldIdx, newIdx);
    onReorder(reordered);
    await axios.post('/reorderQuestionGroups', { order: reordered.map((g, i) => ({ id: g.id, sort_order: i })) });
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={groups.map(g => g.id)} strategy={verticalListSortingStrategy}>
        <Stack spacing={2}>
          {groups.map(g => (
            <SortableGroupCard key={g.id} group={g} onGroupEdited={onGroupEdited}
              onGroupDeleted={onGroupDeleted} optionTemplates={optionTemplates}
              allQuestions={allQuestions} onQuestionsChange={onQuestionsChange} />
          ))}
        </Stack>
      </SortableContext>
    </DndContext>
  );
}

// ─── GroupNameDialog ──────────────────────────────────────────────────────────
function GroupDialog({ open, onClose, onSave, initial, systemTypes, defaultTypeId }) {
  const [name, setName] = useState('');
  const [typeId, setTypeId] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setName(initial?.name || '');
      const t = initial ? initial.elevator_type_id : defaultTypeId;
      setTypeId(t && t !== 'none' ? t : '');
      setSaving(false);
    }
  }, [open, initial, defaultTypeId]);

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(name, typeId || null); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>{initial ? 'Editar grupo' : 'Nuevo grupo de preguntas'}</DialogTitle>
      <Divider />
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField label="Nombre del grupo" fullWidth autoFocus value={name}
            onChange={e => setName(e.target.value)} />
          <FormControl fullWidth>
            <InputLabel id="group-system-type-label">Tipo de sistema</InputLabel>
            <Select labelId="group-system-type-label" id="group-system-type" value={typeId}
              label="Tipo de sistema" onChange={e => setTypeId(e.target.value)}>
              {systemTypes.map(t => (
                <MenuItem key={t.id} value={t.id}>{t.elevatorType}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <LoadingButton variant="contained" loading={saving} disabled={!name.trim() || !typeId} onClick={handleSave} sx={{ borderRadius: 2 }}>
          Guardar
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

// ─── SystemTypeSelector ───────────────────────────────────────────────────────
function SystemTypeSelector({ systemTypes, selected, onChange, counts, unassignedCount }) {
  const options = [
    ...systemTypes.map(t => ({ id: t.id, label: t.elevatorType, count: counts[t.id] || 0 })),
    ...(unassignedCount > 0 ? [{ id: 'none', label: 'Sin tipo asignado', count: unassignedCount, warn: true }] : []),
  ];

  return (
    <Paper elevation={0} sx={{
      p: 2, mb: 3, borderRadius: 3, border: '1px solid #e2e8f0',
      background: 'linear-gradient(135deg, #f8fbff 0%, #eef4ff 100%)',
    }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Box sx={{ width: 28, height: 28, borderRadius: 2, bgcolor: '#2563eb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Cog6ToothIcon style={{ width: 16, height: 16 }} />
        </Box>
        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.2 }}>Tipo de sistema</Typography>
          <Typography variant="caption" color="text.secondary">
            Selecciona el tipo de sistema al que pertenecen los grupos de preguntas.
          </Typography>
        </Box>
      </Stack>

      {options.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No hay tipos de sistema creados. Créalos primero en la pestaña "Tipo de Sistema".
        </Typography>
      ) : (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {options.map(opt => {
            const active = selected === opt.id;
            return (
              <Chip
                key={opt.id}
                id={`system-type-${opt.id}`}
                clickable
                onClick={() => onChange(opt.id)}
                color={opt.warn ? 'warning' : 'primary'}
                variant={active ? 'filled' : 'outlined'}
                label={
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <span>{opt.label}</span>
                    <Box component="span" sx={{
                      px: 0.75, borderRadius: 5, fontSize: '0.7rem', fontWeight: 700,
                      bgcolor: active ? 'rgba(255,255,255,0.25)' : 'rgba(37,99,235,0.08)',
                    }}>{opt.count}</Box>
                  </Stack>
                }
                sx={{
                  fontWeight: 600, borderRadius: 2, height: 34, px: 0.5,
                  transition: 'all .2s ease',
                  bgcolor: active ? undefined : '#fff',
                  boxShadow: active ? '0 4px 12px rgba(37,99,235,0.25)' : 'none',
                  '&:hover': { transform: 'translateY(-1px)' },
                }}
              />
            );
          })}
        </Stack>
      )}
    </Paper>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Preguntas() {
  const [tab, setTab] = useState(0);
  const [groups, setGroups] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [systemTypes, setSystemTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingGroup, setSavingGroup] = useState(false);
  const [gDialog, setGDialog] = useState({ open: false, editing: null });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [gRes, tRes, sRes] = await Promise.all([
        axios.get(`/getQuestionGroups/${company()}`),
        axios.get(`/getOptionTemplateGroups/${company()}`),
        axios.get(`/getElevatorTypes/${company()}`),
      ]);
      const types = sRes.data.data || [];
      setGroups(gRes.data.data || []);
      setTemplates(tRes.data.data || []);
      setSystemTypes(types);
      setSelectedType(prev => prev ?? (types[0]?.id ?? null));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Conteo de grupos por tipo de sistema
  const counts = groups.reduce((acc, g) => {
    if (g.elevator_type_id) acc[g.elevator_type_id] = (acc[g.elevator_type_id] || 0) + 1;
    return acc;
  }, {});
  const unassignedCount = groups.filter(g => !g.elevator_type_id).length;
  const visibleGroups = groups.filter(g =>
    selectedType === 'none' ? !g.elevator_type_id : g.elevator_type_id === selectedType
  );
  const selectedTypeName = selectedType === 'none'
    ? 'Sin tipo asignado'
    : systemTypes.find(t => t.id === selectedType)?.elevatorType;

  // Todas las preguntas de todos los grupos/tipos, para poder copiarlas desde cualquier lugar
  const allQuestions = groups.flatMap(g => (g.questions || []).map(q => ({
    ...q,
    _groupId: g.id,
    _groupName: g.name,
    _typeKey: g.elevator_type_id || 'none',
    _typeName: systemTypes.find(t => t.id === g.elevator_type_id)?.elevatorType || 'Sin tipo asignado',
  })));

  const handleQuestionsChange = useCallback((groupId, qs) => {
    setGroups(prev => prev.map(g => g.id === groupId ? { ...g, questions: qs } : g));
  }, []);

  // Reordenar solo los grupos visibles, manteniendo el resto intacto
  const handleReorder = (reordered) => {
    const ids = new Set(reordered.map(g => g.id));
    setGroups(prev => [...reordered, ...prev.filter(g => !ids.has(g.id))]);
  };

  const handleSaveGroup = async (name, elevatorTypeId) => {
    setSavingGroup(true);
    try {
      const payload = { name, elevator_type_id: elevatorTypeId };
      if (gDialog.editing) {
        const res = await axios.put(`/updateQuestionGroup/${gDialog.editing.id}`, payload);
        const upd = res.data.data;
        setGroups(prev => prev.map(g => g.id === gDialog.editing.id
          ? { ...g, name: upd.name, elevator_type_id: upd.elevator_type_id, systemType: upd.systemType }
          : g));
      } else {
        const res = await axios.post('/saveQuestionGroup', { ...payload, company: company() });
        setGroups(prev => [{ ...res.data.data, questions: [] }, ...prev]);
        if (elevatorTypeId) setSelectedType(elevatorTypeId);
      }
      setGDialog({ open: false, editing: null });
    } catch (e) { console.error(e); }
    finally { setSavingGroup(false); }
  };

  const handleDeleteGroup = async (id) => {
    await axios.delete(`/deleteQuestionGroup/${id}`);
    setGroups(prev => prev.filter(g => g.id !== id));
  };

  return (
    <Box>
      {!loading && (
        <SystemTypeSelector
          systemTypes={systemTypes}
          selected={selectedType}
          onChange={(id) => { setSelectedType(id); setTab(0); }}
          counts={counts}
          unassignedCount={unassignedCount}
        />
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: '1px solid #e2e8f0' }}>
        <Tab label="Grupos de preguntas" />
        <Tab label="Plantillas de opciones" />
      </Tabs>

      {loading ? (
        <Stack alignItems="center" sx={{ py: 8 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Cargando...</Typography>
        </Stack>
      ) : tab === 0 ? (
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Grupos de Preguntas{selectedTypeName ? ` · ${selectedTypeName}` : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedType === 'none'
                  ? 'Estos grupos no tienen tipo de sistema. Edítalos para asignarles uno.'
                  : 'Organiza las preguntas en grupos temáticos para este tipo de sistema.'}
              </Typography>
            </Box>
            <LoadingButton variant="contained" loading={savingGroup} startIcon={<PlusIcon className="h-5 w-5" />}
              disabled={systemTypes.length === 0}
              onClick={() => setGDialog({ open: true, editing: null })} sx={{ borderRadius: 2, px: 3 }}>
              Nuevo grupo
            </LoadingButton>
          </Stack>

          {visibleGroups.length === 0 ? (
            <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '2px dashed #e2e8f0', borderRadius: 4 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {selectedTypeName ? `Sin grupos para "${selectedTypeName}"` : 'Sin grupos aún'}
              </Typography>
              <Button variant="outlined" startIcon={<PlusIcon className="h-4 w-4" />}
                disabled={systemTypes.length === 0}
                onClick={() => setGDialog({ open: true, editing: null })}>
                Crear primer grupo
              </Button>
            </Paper>
          ) : (
            <GroupsDndList groups={visibleGroups} onReorder={handleReorder}
              onGroupEdited={grp => setGDialog({ open: true, editing: grp })}
              onGroupDeleted={handleDeleteGroup}
              optionTemplates={templates}
              allQuestions={allQuestions} onQuestionsChange={handleQuestionsChange} />
          )}

          <GroupDialog open={gDialog.open} onClose={() => setGDialog({ open: false, editing: null })}
            onSave={handleSaveGroup} initial={gDialog.editing}
            systemTypes={systemTypes} defaultTypeId={selectedType} />
        </Box>
      ) : (
        <OptionTemplateManager templates={templates} onTemplatesChange={setTemplates} />
      )}
    </Box>
  );
}
